import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { IgButton, IgDialog, IgTypography } from "@intelligems/ig-theme";
import { OrganizationDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { organizationScriptStatusKey } from "@src/api/queryKeys";
import { useGetScriptStatus } from "@src/api/shops/get-script-status";
import { isDialogOpen, setDialog } from "@src/store/slices/global";
import { copyToClipboard } from "@src/utils/copyToClipboard";
import { intelligemsFullThemeSnippet } from "@src/utils/intelligemsThemeSnippets";
import { addTemplateSnippet } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";
import { useQueryClient } from "@tanstack/react-query";

interface TemplateTestGateDialogProps {
  organization: OrganizationDto;
}

export const TemplateTestGateDialog = ({
  organization,
}: TemplateTestGateDialogProps) => {
  const scriptStatus = useGetScriptStatus(organization.id);
  const queryClient = useQueryClient();

  const isOpen = useSelector(isDialogOpen("templateSnippetGate"));
  const dispatch = useDispatch();
  const handleClose = () => {
    if (isOpen) {
      dispatch(setDialog({ dialog: "templateSnippetGate", open: false }));
    }
  };
  const onScriptCopy = async () => {
    await copyToClipboard(`${intelligemsFullThemeSnippet}`);
    toast.success("Script Copied!");
  };

  useEffect(() => {
    if (
      scriptStatus.isSuccess &&
      (scriptStatus.data.type === "appEmbedFound" ||
        scriptStatus.data.type === "inlineFound" ||
        scriptStatus.data.type === "inlineAsyncFound" ||
        scriptStatus.data.type === "multipleScriptsWithAppEmbedFound" ||
        scriptStatus.data.type === "multipleScriptsFound" ||
        scriptStatus.data.type === "missing" ||
        scriptStatus.data.type === "incorrectScriptFound") &&
      scriptStatus.data.hasTemplateScript
    ) {
      handleClose();
    }
  }, [scriptStatus, isOpen]);
  return (
    <IgDialog
      title={"Please add the Intelligems template snippet"}
      handleClose={handleClose}
      showDialog={isOpen}
      showCloseIcon={true}
      hideActions={true}
      dialogContentWidth={"450px"}
      maxWidth={"md"}
      dialogHeight={"250px"}
      yesButtonText={null}
      noButtonText={null}
      rawContent={
        <Grid item container direction={"column"} rowSpacing={2}>
          <Grid item mb={2.5}>
            <IgTypography>
              In order for template tests to run properly, please add the
              Intelligems template script to your theme code.{" "}
              <IgTypography
                onClick={() => {
                  openNewTab(addTemplateSnippet, "Intelligems Theme Snippets");
                }}
                igStyle={"link"}
              >
                Learn More.
              </IgTypography>
            </IgTypography>
          </Grid>
          <Grid
            item
            container
            direction={"row"}
            justifyContent={"center"}
            columnSpacing={2}
          >
            <Grid item>
              <IgButton
                loading={scriptStatus.isFetching}
                variant={"contained"}
                color={"error"}
                onClick={() =>
                  void queryClient.invalidateQueries({
                    queryKey: organizationScriptStatusKey.org(organization.id),
                  })
                }
              >
                Recheck
              </IgButton>
            </Grid>
            <Grid item>
              <IgButton
                variant={"contained"}
                onClick={() => {
                  void onScriptCopy();
                }}
              >
                Copy Script
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      }
    />
  );
};
